import { Component, EventEmitter, Output, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { AuthService, CurrentUser, DataService } from '../../services';
import { CleaningStatusService, CleaningStatusEvent, CleaningCompletedEvent } from '../../services/cleaning-status.service';
import { CleaningSnackbarComponent, CleaningSnackbarData } from '../../components/cleaning-snackbar/cleaning-snackbar.component';
import { DialogService } from '../../services/dialog.service';
import { UsageSummaryDialogComponent } from '../../components/usage-summary-dialog/usage-summary-dialog.component';
import { LayoutService, ViewMode } from '../../services/layout.service';
import { UserMode } from '../../models/user';
import { Subscription } from 'rxjs';
import { environment } from '../../../environments/environment.development';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatMenuModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatTooltipModule,
    MatSnackBarModule,
    UsageSummaryDialogComponent,
    FormsModule
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Output() toggleSidebar = new EventEmitter<void>();
  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;
  
  currentUser: CurrentUser | null = null;
  searchExpanded = false;
  searchQuery = '';
  viewMode: ViewMode = 'simple';
  cleaningStatuses: CleaningStatusEvent[] = [];
  cleaningDropdownOpen = false;
  environmentName: string = '';
  private userSubscription?: Subscription;
  private viewModeSubscription?: Subscription;
  private cleaningStatusSub?: Subscription;
  private cleaningCompletedSub?: Subscription;
  private clearStatusTimers = new Map<string, ReturnType<typeof setTimeout>>();
  showUserMenu = false;
  private documentClickListener: any;
  showHelpMenu = false;

  /**
   * Get tooltip text for environment badge
   */
  get environmentTooltip(): string {
    return `Environment: ${environment.name}\nAPI: ${environment.apiUrl}`;
  }

  /**
   * Get the count of cleaning statuses to display
   */
  get visibleCleaningStatuses(): CleaningStatusEvent[] {
    const currentUrl = this.router.url;
    return this.cleaningStatuses.filter(status => {
      const isOnProjectCanvas = currentUrl === `/project-canvas/${status.projectId}`;
      return !isOnProjectCanvas;
    });
  }

  /**
   * Check if we should show the cleaning status indicator.
   */
  get shouldShowCleaningStatus(): boolean {
    return this.visibleCleaningStatuses.length > 0;
  }

  /**
   * Check if we should show the multi-clean summary (more than one clean)
   */
  get shouldShowMultiCleanSummary(): boolean {
    return this.visibleCleaningStatuses.length > 1;
  }

  /**
   * Get the single cleaning status (when there's only one)
   */
  get singleCleaningStatus(): CleaningStatusEvent | null {
    return this.visibleCleaningStatuses.length === 1 ? this.visibleCleaningStatuses[0] : null;
  }

  /**
   * Get the count of in-progress cleans
   */
  get inProgressCount(): number {
    return this.visibleCleaningStatuses.filter(s => s.state === 'in-progress').length;
  }

  constructor(
    private router: Router,
    private authService: AuthService,
    private dataService: DataService,
    private layoutService: LayoutService,
    private dialogService: DialogService,
    private cleaningStatusService: CleaningStatusService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    // Set environment name for display
    if (environment.name === 'uat') {
      this.environmentName = 'UAT';
    } else if (environment.name === 'development') {
      this.environmentName = 'DEV';
    }
    // Production: environmentName remains empty string (no badge shown)

    // Subscribe to current user changes
    this.userSubscription = this.authService.currentUser$.subscribe(user => {
      this.currentUser = user;
    });

    // Subscribe to view mode changes
    this.viewModeSubscription = this.layoutService.viewMode$.subscribe(mode => {
      this.viewMode = mode;
    });

    // Subscribe to cleaning statuses
    this.cleaningStatusSub = this.cleaningStatusService.statuses$.subscribe(statusesMap => {
      this.cleaningStatuses = Array.from(statusesMap.values());
      
      // Handle auto-clearing of completed/failed statuses
      this.cleaningStatuses.forEach(status => {
        if (status.state === 'success' || status.state === 'error') {
          // Clear any existing timer for this project
          const existingTimer = this.clearStatusTimers.get(status.projectId);
          if (existingTimer) {
            clearTimeout(existingTimer);
          }
          
          // Set new timer to remove this status after 5 seconds
          const timer = setTimeout(() => {
            this.cleaningStatusService.removeStatus(status.projectId);
            this.clearStatusTimers.delete(status.projectId);
          }, 5000);
          
          this.clearStatusTimers.set(status.projectId, timer);
        }
      });
    });

    this.cleaningCompletedSub = this.cleaningStatusService.completed$.subscribe(event => {
      this.showCleaningCompletedSnackbar(event);
    });

    // Global click handler to close the user menu when clicking outside
    this.documentClickListener = (event: Event) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.user-menu-dropdown') && !target.closest('.user-icon-btn')) {
        this.showUserMenu = false;
      }
      if (!target.closest('.help-menu-dropdown') && !target.closest('.help-icon-btn')) {
        this.showHelpMenu = false;
      }
    };
    document.addEventListener('click', this.documentClickListener);
  }

  ngOnDestroy(): void {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
    if (this.viewModeSubscription) {
      this.viewModeSubscription.unsubscribe();
    }
    if (this.cleaningStatusSub) {
      this.cleaningStatusSub.unsubscribe();
    }
    if (this.cleaningCompletedSub) {
      this.cleaningCompletedSub.unsubscribe();
    }
    if (this.documentClickListener) {
      document.removeEventListener('click', this.documentClickListener);
    }
    // Clear all timers
    this.clearStatusTimers.forEach(timer => clearTimeout(timer));
    this.clearStatusTimers.clear();
  }

  private showCleaningCompletedSnackbar(event: CleaningCompletedEvent): void {
    this.snackBar.openFromComponent(CleaningSnackbarComponent, {
      data: {
        message: `Cleaning for ${event.projectName} has finished`,
        type: 'success',
        showViewResults: event.responseData !== null && event.responseData !== undefined,
        responseData: event.responseData,
        projectName: event.projectName,
        projectId: event.projectId
      } as CleaningSnackbarData,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: 'success-snackbar-custom',
      duration: 10000
    });
  }

  onToggleSidebar() {
    this.toggleSidebar.emit();
  }

  onProfile() {
    this.router.navigate(['/profile']);
  }

  onManageSubscription() {
    this.router.navigate(['/subscription']);
  }

  onLogout() {
    // Call logout API endpoint, then clear authentication and navigate to login
    this.dataService.logoutUser().subscribe({
      next: () => {
        this.authService.logout();
        this.router.navigate(['/login']);
      },
      error: (error) => {
        console.error('Logout API call failed:', error);
        // Still logout locally even if API call fails
        this.authService.logout();
        this.router.navigate(['/login']);
      }
    });
  }

  onLogoClick() {
    this.router.navigate(['/dashboard']);
  }

  toggleSearch() {
    this.searchExpanded = !this.searchExpanded;
    if (!this.searchExpanded) {
      this.searchQuery = '';
    } else {
      // Focus the input after the animation starts
      setTimeout(() => {
        this.searchInput?.nativeElement.focus();
      }, 100);
    }
  }

  onSearchSubmit() {
    if (this.searchQuery.trim()) {
      // TODO: Implement search functionality
      console.log('Searching for:', this.searchQuery);
    }
  }

  onHelp() {
    // Determine which help section to open based on current route
    const currentUrl = this.router.url;
    let feature: string | undefined;

    if (currentUrl.includes('/data-source-profile') || currentUrl.includes('/activity')) {
      feature = 'profile-data';
    } else if (currentUrl.includes('/anomaly-detection')) {
      feature = 'anomaly-detection';
    } else if (currentUrl.includes('/clean-data') || currentUrl.includes('/project-canvas')) {
      feature = 'clean-data';
    }

    // Open help dialog
    this.dialogService.openHelpDialog(undefined, feature);
  }

  setViewMode(mode: ViewMode) {
    this.layoutService.setViewMode(mode);
    
    // Update user settings on the backend
    const userMode = mode === 'advanced' ? UserMode.Advanced : UserMode.Simple;
    this.dataService.updateUserSettings({ userMode }).subscribe({
      next: () => {
        // Update local auth service
        this.authService.setUserSettings({ userMode });
      },
      error: (error) => {
        console.error('Failed to update user settings:', error);
        // Still update local state even if API call fails
        this.authService.setUserSettings({ userMode });
      }
    });
  }

  getUserFullName(): string {
    if (!this.currentUser) {
      return 'Guest';
    }
    const firstName = this.currentUser.firstName || '';
    const lastName = this.currentUser.lastName || '';
    return `${firstName} ${lastName}`.trim() || this.currentUser.username || 'Guest';
  }

  onChangePassword() {
    this.router.navigate(['/change-password']);
  }

  onUserManagement() {
    this.showUserMenu = false;
    this.router.navigate(['/user-management']);
  }

  toggleUserMenu(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.showUserMenu = !this.showUserMenu;
    if (this.showUserMenu) {
      this.showHelpMenu = false;
    }
  }

  toggleHelpMenu(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.showHelpMenu = !this.showHelpMenu;
    if (this.showHelpMenu) {
      this.showUserMenu = false;
    }
  }

  openUsageSummary(event?: Event) {
    // If invoked from an anchor, prevent default navigation
    if (event) {
      event.preventDefault();
    }
    // Open the usage/plan details dialog
    this.dialogService.openDialog(UsageSummaryDialogComponent, { width: '640px' });
  }

  toggleCleaningDropdown() {
    this.cleaningDropdownOpen = !this.cleaningDropdownOpen;
  }

  closeCleaningDropdown() {
    this.cleaningDropdownOpen = false;
  }
}
